import { useCallback, useEffect, useState } from 'react'
import { Text } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { useBookmarks } from '@/lib/bookmarks-context'
import { ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import { useTicketHeader } from '@/lib/ticket-header-context'
import type { TicketDetailTicket } from '@/types/api'

type UseTicketHeaderControlsParams = {
  ticket: TicketDetailTicket | null
  copyToClipboard: (text: string, okMessage?: string) => Promise<void>
}

export function useTicketHeaderControls({
  ticket,
  copyToClipboard,
}: UseTicketHeaderControlsParams) {
  const { isBookmarked, toggleBookmark } = useBookmarks()
  const bookmarked = ticket ? isBookmarked(ticket.id) : false
  const [bookmarkBusy, setBookmarkBusy] = useState(false)

  const onBookmark = useCallback(async () => {
    if (!ticket || bookmarkBusy) return
    setBookmarkBusy(true)
    try {
      await toggleBookmark(ticket.id)
    } finally {
      setBookmarkBusy(false)
    }
  }, [bookmarkBusy, ticket, toggleBookmark])

  const ticketHeader = useTicketHeader()
  const setHeaderControls = ticketHeader?.setControls

  const onHeaderBookmark = useCallback(() => {
    openConfirmModal({
      title: bookmarked ? 'Убрать из закладок?' : 'Добавить в закладки?',
      children: (
        <Text size="sm" c="dimmed">
          {bookmarked
            ? 'Обращение будет удалено из ваших закладок.'
            : 'Обращение будет добавлено в закладки.'}
        </Text>
      ),
      labels: { confirm: bookmarked ? 'Убрать' : 'Добавить', cancel: 'Отмена' },
      onConfirm: () => void onBookmark(),
    })
  }, [bookmarked, onBookmark])

  const onHeaderCopyLink = useCallback(() => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}`
    void copyToClipboard(url, 'Ссылка скопирована')
  }, [copyToClipboard])

  useEffect(() => {
    if (!setHeaderControls) return
    if (!ticket) {
      setHeaderControls(null)
      return
    }
    setHeaderControls({
      bookmarked,
      bookmarkBusy,
      onBookmarkClick: onHeaderBookmark,
      onCopyLink: onHeaderCopyLink,
    })
    return () => setHeaderControls(null)
  }, [bookmarkBusy, bookmarked, onHeaderBookmark, onHeaderCopyLink, setHeaderControls, ticket])

  const openTicketInNewTab = useCallback(() => {
    if (!ticket) return
    const ref = ticketPathSegmentFromListItem({
      id: ticket.id,
      ticket_number: ticket.ticket_number,
      project_slug: ticket.project_slug,
    })
    const path = `/tickets/${encodeURIComponent(ref)}`
    window.open(path, '_blank', 'noopener,noreferrer')
  }, [ticket])

  return { openTicketInNewTab }
}
